import { format, isValid, parseISO } from 'date-fns';
import { AskCustomerButton } from '../../customerRequests/CustomerRequestComposer';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { cn } from '../../../lib/utils';
import {
  borderPrimary,
  cardClass,
  lightGreenBG,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  tertiaryText,
} from '../../../styling/stylingHelper';
import { AdminLayout } from './AdminLayout';
import { ResponsiveTable, MobileCardField, MobileCardList } from '../../shared/mobile/ResponsiveTable';
import {
  ALL_SUPPLIERS_SCOPE_KEY,
  dataAlertScopeSummary,
  dataAlertTypeLabel,
  getDataAlerts,
  recheckDataAlert,
  resolveDataAlert,
  runDataAlertDetectors,
  DATA_ALERT_TYPES,
  DATA_ALERT_STATUSES,
  type DataAlertRow,
  type DataAlertStatus,
  type DataAlertResolutionKind,
  type DataAlertRunResult,
} from '../../../services/admin/dataAlertsAdminService';
import { currencyFormatter } from '../../../services/helperService';
import { getTableFormattedDate } from '../../../utils/dateUtils';

const STATUS_LABEL_KEYS: Record<DataAlertStatus, string> = {
  open: 'adminTesting.dataAlerts.statusOpen',
  resolved: 'adminTesting.dataAlerts.statusResolved',
  auto_resolved: 'adminTesting.dataAlerts.statusAutoResolved',
};

const RESOLUTION_LABEL_KEYS: Record<DataAlertResolutionKind, string> = {
  accepted: 'adminTesting.dataAlerts.resolutionAccepted',
  false_positive: 'adminTesting.dataAlerts.resolutionFalsePositive',
};

/** The endpoint returns every matching alert, so the incremental render is client-side. Resolved
 *  rows are never deleted — without a cap this list grows for as long as the detectors run. */
const PAGE_SIZE = 50;

/** Date + time — the detector reruns daily, so the time separates one run from the next. */
function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return `${getTableFormattedDate(date)} ${time}`;
}

// `payload` is a loose jsonb blob written by the detectors — read every field defensively, an
// older row can be missing any of them.
function payloadString(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

function payloadNumber(payload: Record<string, unknown>, key: string): number | null {
  const value = payload[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function payloadStringArray(payload: Record<string, unknown>, key: string): string[] {
  const value = payload[key];
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === 'string');
}

/**
 * `hitCount` arrives from the detectors already formatted as its own ratio ('7/8'), so it is
 * rendered verbatim and never recombined with `windowWeeks` — that would print "7/8 of 8".
 * `windowWeeks` only ever labels the detection window on its own line.
 */
function hitRateLines(payload: Record<string, unknown>, hitRateKey: string, t: TFunction): string[] {
  const lines: string[] = [];
  const hitCount = payloadString(payload, 'hitCount');
  if (hitCount) lines.push(t(hitRateKey, { hitCount }));
  const windowWeeks = payloadNumber(payload, 'windowWeeks');
  if (windowWeeks !== null) {
    lines.push(t('adminTesting.dataAlerts.summaryWindow', { windowWeeks }));
  }
  return lines;
}

/**
 * Human-readable rendering of the payload, one line per fact. Unknown types (and known types
 * whose payload is empty) return nothing — the raw JSON below it is then the whole detail.
 */
function summaryLines(row: DataAlertRow, t: TFunction): string[] {
  const payload = row.payload ?? {};
  const lines: string[] = [];

  switch (row.alertType) {
    case 'missing_revenue_day': {
      lines.push(t('adminTesting.dataAlerts.summaryMissingRevenueDay', {
        weekday: payloadString(payload, 'weekday') ?? row.periodKey,
      }));
      lines.push(...hitRateLines(payload, 'adminTesting.dataAlerts.summaryRevenueHitRate', t));
      const sources = payloadStringArray(payload, 'sources');
      if (sources.length > 0) {
        lines.push(t('adminTesting.dataAlerts.summarySources', { sources: sources.join(', ') }));
      }
      const automationStatus = payloadString(payload, 'automationStatus');
      if (automationStatus) {
        lines.push(t('adminTesting.dataAlerts.summaryAutomationStatus', { status: automationStatus }));
      }
      const automationError = payloadString(payload, 'automationError');
      if (automationError) {
        lines.push(t('adminTesting.dataAlerts.summaryAutomationError', { error: automationError }));
      }
      return lines;
    }

    case 'missing_supplier_week': {
      // The ALL_SUPPLIERS collapse carries no hitCount — it is not one brand's hit rate — but it
      // does carry the brands it stands in for, which is the useful detail there.
      const isAllSuppliers = row.scopeKey === ALL_SUPPLIERS_SCOPE_KEY;
      lines.push(isAllSuppliers
        ? t('adminTesting.dataAlerts.summaryMissingSupplierWeekAll', { week: row.periodKey })
        : t('adminTesting.dataAlerts.summaryMissingSupplierWeek', {
            supplier: row.scopeKey,
            week: row.periodKey,
          }));
      if (isAllSuppliers) {
        // Shared helper, so every surface names the collapse identically.
        const scope = dataAlertScopeSummary(row, (key, vars) => t(key, vars));
        if (scope) lines.push(scope);
      }
      lines.push(...hitRateLines(payload, 'adminTesting.dataAlerts.summarySupplierHitRate', t));
      const medianWeeklySpend = payloadNumber(payload, 'medianWeeklySpend');
      if (medianWeeklySpend !== null) {
        // Raw orderhistory totals, so pre-factuur-override: a ranking hint, never a spend figure.
        lines.push(t('adminTesting.dataAlerts.summaryOrderVolume', {
          amount: currencyFormatter.format(medianWeeklySpend),
        }));
      }
      const platforms = payloadStringArray(payload, 'platforms');
      if (platforms.length > 0) {
        lines.push(t('adminTesting.dataAlerts.summaryPlatforms', { platforms: platforms.join(', ') }));
      }
      return lines;
    }

    // Raised at send time by the count trigger, not by a detector: the client finalized a
    // count and the report could not be built from it.
    case 'inventory_report_skipped': {
      lines.push(t('adminTesting.dataAlerts.summaryInventoryReportSkipped', {
        reportType: payloadString(payload, 'reportType') ?? row.scopeKey,
        group: payloadString(payload, 'groupName') || t('adminTesting.dataAlerts.unknownGroup'),
        date: payloadString(payload, 'countDate') ?? row.periodKey,
      }));
      const reason = payloadString(payload, 'reason');
      if (reason) lines.push(t('adminTesting.dataAlerts.summarySkipReason', { reason }));
      // No recheck button on this type, so say how it clears.
      lines.push(t('adminTesting.dataAlerts.summarySkipClears'));
      return lines;
    }

    // HISTORIC type — phase 8 stopped holding reports, so nothing writes these any more. The
    // old rows stay readable (and hand-resolvable); there is nothing left to recheck or release.
    case 'report_held': {
      lines.push(t('adminTesting.dataAlerts.summaryReportHeld', {
        reportType: payloadString(payload, 'reportType') ?? row.scopeKey,
        week: row.periodKey,
        // Not named `count`: i18next reads that as a plural selector and looks for _one/_other.
        alertCount: payloadStringArray(payload, 'blockingAlertIds').length,
      }));
      lines.push(t('adminTesting.dataAlerts.summaryReportHeldHistoric'));
      return lines;
    }

    default:
      return lines;
  }
}

function typeLabel(alertType: string, t: TFunction): string {
  return dataAlertTypeLabel(alertType, (key) => t(key));
}

/** Open needs attention; both resolved states are done and stay muted — the label separates them. */
function StatusChip({ status, label }: { status: DataAlertStatus; label: string }) {
  return (
    <span className={cn(
      'text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap',
      status === 'open'
        ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
    )}>
      {label}
    </span>
  );
}

/** Prose per type plus the raw payload — the JSON is what covers fields this page doesn't know. */
function AlertDetail({ row, t }: { row: DataAlertRow; t: TFunction }) {
  const lines = summaryLines(row, t);
  const hasPayload = row.payload != null && Object.keys(row.payload).length > 0;

  return (
    <div className="space-y-2">
      {lines.length > 0 ? (
        lines.map((line, index) => (
          <p key={index} className={cn(primaryText, smallTextSize)}>{line}</p>
        ))
      ) : (
        <p className={cn(secondaryText, smallTextSize)}>{t('adminTesting.dataAlerts.noSummary')}</p>
      )}

      <p className={cn(tertiaryText, 'text-xs')}>
        {t('adminTesting.dataAlerts.detailScope', { scope: row.scopeKey, period: row.periodKey })}
        {' · '}
        {t('adminTesting.dataAlerts.detailLastChecked', { at: formatDateTime(row.lastCheckedAt) })}
      </p>

      {hasPayload && (
        <p className={cn(tertiaryText, 'text-xs font-mono break-all')}>
          {JSON.stringify(row.payload)}
        </p>
      )}
    </div>
  );
}

const formInputClass = cn(
  'w-full px-3 py-2 rounded-lg border',
  'border-gray-300 dark:border-gray-600',
  'bg-white dark:bg-gray-700',
  'disabled:opacity-50 disabled:cursor-not-allowed',
  primaryText, smallTextSize
);

const primaryButtonClass = cn(
  'px-4 py-2 rounded-lg transition-colors',
  'bg-purple-600 dark:bg-purple-600 text-white dark:text-white',
  'hover:bg-purple-700 dark:hover:bg-purple-700',
  'disabled:opacity-40 disabled:cursor-not-allowed',
  smallTextSize, semiBoldText
);

const rowButtonClass = cn(
  'px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap',
  'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600',
  'disabled:opacity-50 disabled:cursor-not-allowed',
  primaryText, 'text-xs'
);

/** What the last recheck of a given row reported — cleared rows say nothing, their chip moved. */
type RecheckOutcome = { id: string; kind: 'still_missing' | 'error' };

/**
 * Details / Recheck / Resolve, shared by the table row and the mobile card so the two can't
 * drift. Recheck is hidden for anything already closed and for the two DETECTORLESS types —
 * the historic `report_held` and `inventory_report_skipped` — where the server answers
 * 'unsupported' with a 400. Those rows are read-only apart from resolving them by hand: a
 * skipped inventory report clears itself when the next count for that group sends.
 */
const RECHECKABLE_ALERT_TYPES = (alertType: string): boolean =>
  alertType !== 'report_held' && alertType !== 'inventory_report_skipped';
function RowActions({
  row,
  expanded,
  recheckingId,
  recheckOutcome,
  onToggle,
  onRecheck,
  onResolve,
  t,
  className,
}: {
  row: DataAlertRow;
  expanded: boolean;
  recheckingId: string | null;
  recheckOutcome: RecheckOutcome | null;
  onToggle: () => void;
  onRecheck: () => void;
  onResolve: () => void;
  t: TFunction;
  className?: string;
}) {
  const isOpen = row.status === 'open';
  const canRecheck = isOpen && RECHECKABLE_ALERT_TYPES(row.alertType);
  const busy = recheckingId === row.id;
  const weekStart = parseISO(`${row.periodKey}-1`);
  const requestPeriodStart = isValid(weekStart) ? format(weekStart, 'yyyy-MM-dd') : undefined;
  const outcome = recheckOutcome?.id === row.id ? recheckOutcome.kind : null;

  return (
    <span className={cn('flex flex-wrap items-center gap-2', className)}>
      {row.alertType === 'missing_supplier_week' && row.restaurantId && <AskCustomerButton seed={{ restaurantId: row.restaurantId, type: 'supplier_order_confirmation', supplierLabel: row.scopeKey === ALL_SUPPLIERS_SCOPE_KEY ? undefined : row.scopeKey, periodStart: requestPeriodStart, source: { kind: 'data_alert', id: row.id } }} />}
      <button onClick={onToggle} className={cn(secondaryText, 'text-xs underline')}>
        {expanded
          ? t('adminTesting.dataAlerts.hideDetails')
          : t('adminTesting.dataAlerts.showDetails')}
      </button>
      {canRecheck && (
        // Only one recheck at a time: they hit the same detector, and a second spinner in the
        // list would leave no way to tell which row the next answer belongs to.
        <button onClick={onRecheck} disabled={recheckingId !== null} className={rowButtonClass}>
          {busy
            ? t('adminTesting.dataAlerts.recheckBusy')
            : t('adminTesting.dataAlerts.recheckAction')}
        </button>
      )}
      {isOpen && (
        <button onClick={onResolve} className={rowButtonClass}>
          {t('adminTesting.dataAlerts.resolveAction')}
        </button>
      )}
      {outcome && (
        <span className={cn(
          'text-xs',
          outcome === 'error' ? 'text-red-600 dark:text-red-400' : tertiaryText
        )}>
          {outcome === 'error'
            ? t('adminTesting.dataAlerts.recheckError')
            : t('adminTesting.dataAlerts.recheckStillMissing')}
        </span>
      )}
    </span>
  );
}

/** Kind + note + when, shown on every row the detector no longer owns. */
function ResolutionNote({ row, t, className }: { row: DataAlertRow; t: TFunction; className?: string }) {
  if (row.status === 'open') return null;

  const kindLabel = row.resolutionKind
    ? t(RESOLUTION_LABEL_KEYS[row.resolutionKind])
    : t('adminTesting.dataAlerts.resolutionAuto');

  return (
    <span className={cn(secondaryText, 'text-xs', className)}>
      {kindLabel}
      {row.resolvedAt && ` · ${formatDateTime(row.resolvedAt)}`}
      {row.resolutionNote && (
        <span className={cn(tertiaryText, 'block break-words')}>{row.resolutionNote}</span>
      )}
    </span>
  );
}

/**
 * Resolve dialog. Both actions only close the alert — neither sends anything, which is why the
 * dialog says so: the send stays a manual step on the Send Reports tab.
 */
function ResolveDialog({
  alert,
  onClose,
  onResolved,
  t,
}: {
  alert: DataAlertRow;
  onClose: () => void;
  onResolved: (updated: DataAlertRow) => void;
  t: TFunction;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (kind: DataAlertResolutionKind) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await resolveDataAlert(alert.id, kind, note.trim() || undefined);
      onResolved(updated);
      onClose();
    } catch (err) {
      console.error('Failed to resolve data alert:', err);
      setError(t('adminTesting.dataAlerts.resolveError'));
      setBusy(false);
    }
  };

  const actionButtonClass = cn(
    'px-4 py-2 rounded-lg transition-colors text-left',
    'disabled:opacity-40 disabled:cursor-not-allowed',
    smallTextSize, semiBoldText
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/50 p-4"
      onClick={onClose}
    >
      <div className={cn(cardClass, 'w-full max-w-md p-6 space-y-4')} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className={cn(primaryText, semiBoldText)}>{t('adminTesting.dataAlerts.resolveTitle')}</h3>
            <p className={cn(secondaryText, 'text-xs mt-1')}>
              {typeLabel(alert.alertType, t)} · {alert.restaurantName} · {alert.periodKey}
            </p>
          </div>
          <button
            onClick={onClose}
            className={cn(secondaryText, 'hover:opacity-70')}
            aria-label={t('adminTesting.dataAlerts.resolveClose')}
          >
            ✕
          </button>
        </div>

        <p className={cn(secondaryText, 'text-xs')}>{t('adminTesting.dataAlerts.resolveHint')}</p>

        {error && <div className="text-xs text-red-600 dark:text-red-400">{error}</div>}

        <div>
          <label className={cn(secondaryText, 'text-xs block mb-1')}>
            {t('adminTesting.dataAlerts.resolveNoteLabel')}
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder={t('adminTesting.dataAlerts.resolveNotePlaceholder')}
            className={cn(
              'w-full px-3 py-2 rounded-lg border resize-none',
              'border-gray-300 dark:border-gray-600',
              'bg-white dark:bg-gray-700',
              primaryText, smallTextSize
            )}
          />
        </div>

        <div className="flex flex-col gap-2">
          <button
            onClick={() => submit('accepted')}
            disabled={busy}
            className={cn(
              actionButtonClass,
              'bg-purple-600 dark:bg-purple-600 text-white dark:text-white',
              'hover:bg-purple-700 dark:hover:bg-purple-700'
            )}
          >
            {t('adminTesting.dataAlerts.resolveAccepted')}
          </button>
          <button
            onClick={() => submit('false_positive')}
            disabled={busy}
            className={cn(
              actionButtonClass,
              'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600',
              primaryText
            )}
          >
            {t('adminTesting.dataAlerts.resolveFalsePositive')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Runs the detectors on demand — the daily cron's work, on request. The optional range exists for
 * catching up on days the daily run has already moved past; every rule about what makes a range
 * legal lives on the server, so this only enforces "both dates filled" and shows what it answers.
 */
function RunDetectorsDialog({
  onClose,
  onComplete,
  t,
}: {
  onClose: () => void;
  onComplete: (result: DataAlertRunResult) => void;
  t: TFunction;
}) {
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rangeComplete = startDate !== '' && endDate !== '';
  const canRun = !busy && (!useCustomRange || rangeComplete);

  const submit = async () => {
    if (!canRun) return;
    setBusy(true);
    setError(null);
    try {
      const result = await runDataAlertDetectors(
        useCustomRange ? { startDate, endDate } : undefined
      );
      onComplete(result);
      onClose();
    } catch (err) {
      console.error('Failed to run the data alert detectors:', err);
      // The server's own message names which rule the range broke — far more useful than ours.
      setError(err instanceof Error && err.message
        ? err.message
        : t('adminTesting.dataAlerts.runError'));
      setBusy(false);
    }
  };

  const optionClass = cn('flex items-start gap-2 cursor-pointer', primaryText, smallTextSize);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/50 p-4"
      onClick={onClose}
    >
      <div className={cn(cardClass, 'w-full max-w-md p-6 space-y-4')} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className={cn(primaryText, semiBoldText)}>{t('adminTesting.dataAlerts.runTitle')}</h3>
            <p className={cn(secondaryText, 'text-xs mt-1')}>{t('adminTesting.dataAlerts.runHint')}</p>
          </div>
          <button
            onClick={onClose}
            className={cn(secondaryText, 'hover:opacity-70')}
            aria-label={t('adminTesting.dataAlerts.runClose')}
          >
            ✕
          </button>
        </div>

        {error && <div className="text-xs text-red-600 dark:text-red-400">{error}</div>}

        <div className="space-y-3">
          <label className={optionClass}>
            <input
              type="radio"
              name="dataAlertRunMode"
              checked={!useCustomRange}
              onChange={() => setUseCustomRange(false)}
              disabled={busy}
              className="mt-0.5"
            />
            <span>{t('adminTesting.dataAlerts.runModeStandard')}</span>
          </label>

          <label className={optionClass}>
            <input
              type="radio"
              name="dataAlertRunMode"
              checked={useCustomRange}
              onChange={() => setUseCustomRange(true)}
              disabled={busy}
              className="mt-0.5"
            />
            <span>{t('adminTesting.dataAlerts.runModeCustom')}</span>
          </label>

          {/* Rendered only under the custom option: two permanently greyed inputs read as broken. */}
          {useCustomRange && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-6">
              <div>
                <label className={cn(secondaryText, 'text-xs block mb-1')}>
                  {t('adminTesting.dataAlerts.runStartLabel')}
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={busy}
                  className={formInputClass}
                />
              </div>
              <div>
                <label className={cn(secondaryText, 'text-xs block mb-1')}>
                  {t('adminTesting.dataAlerts.runEndLabel')}
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={busy}
                  className={formInputClass}
                />
              </div>
            </div>
          )}
        </div>

        <button onClick={submit} disabled={!canRun} className={cn(primaryButtonClass, 'w-full')}>
          {busy ? t('adminTesting.dataAlerts.runBusy') : t('adminTesting.dataAlerts.runSubmit')}
        </button>
      </div>
    </div>
  );
}

/** Missing-data alerts from the daily detectors, including the sends they held back. */
const AdminCommunicationDataAlertsPage = () => {
  // Super-admin tooling is always English regardless of the app's selected language.
  const { i18n } = useTranslation();
  // getFixedT returns a new function identity every call — memoize it, or the load
  // effect below re-runs on every render (infinite fetch loop).
  const t = useMemo(() => i18n.getFixedT('en'), [i18n]);

  const [alertType, setAlertType] = useState('');
  const [status, setStatus] = useState('');
  const [restaurantQuery, setRestaurantQuery] = useState('');

  const [rows, setRows] = useState<DataAlertRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [resolving, setResolving] = useState<DataAlertRow | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [recheckingId, setRecheckingId] = useState<string | null>(null);
  const [recheckOutcome, setRecheckOutcome] = useState<RecheckOutcome | null>(null);
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [runResult, setRunResult] = useState<DataAlertRunResult | null>(null);
  // Bumped after a detector run so the effect below refetches: the run is what creates and
  // auto-resolves rows, so the list on screen is stale the moment it returns.
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const loadAlerts = async () => {
      try {
        setLoading(true);
        setError(false);
        const alerts = await getDataAlerts({
          type: alertType || undefined,
          status: (status || undefined) as DataAlertStatus | undefined,
        });
        if (cancelled) return;
        setRows(alerts);
      } catch (err) {
        console.error('Failed to load data alerts:', err);
        if (cancelled) return;
        setRows([]);
        setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadAlerts();
    return () => { cancelled = true; };
  }, [alertType, status, reloadToken]);

  // Restaurant filtering is client-side on the denormalized name: alerts outlive their
  // restaurant, so a name a live-restaurant dropdown no longer offers must still be findable.
  // Sorting newest-first here too, so the order holds whatever the endpoint returns.
  const visibleRows = useMemo(() => {
    const query = restaurantQuery.trim().toLowerCase();
    const filtered = query === ''
      ? rows
      : rows.filter((row) => (row.restaurantName ?? '').toLowerCase().includes(query));
    return [...filtered].sort(
      (a, b) => new Date(b.detectedAt).getTime() - new Date(a.detectedAt).getTime()
    );
  }, [rows, restaurantQuery]);

  const pagedRows = visibleRows.slice(0, visibleCount);
  const hasMore = visibleRows.length > pagedRows.length;

  const hasFilters = alertType !== '' || status !== '' || restaurantQuery.trim() !== '';

  /** Every filter change restarts at the first page — a count into the old result set is
   *  meaningless once the set changes. */
  const applyFilter = (setter: (value: string) => void, value: string) => {
    setter(value);
    setVisibleCount(PAGE_SIZE);
  };

  const clearFilters = () => {
    setAlertType('');
    setStatus('');
    setRestaurantQuery('');
    setVisibleCount(PAGE_SIZE);
  };

  /** Resolve and recheck both return the updated row — swap it in rather than refetching the
   *  list. It stays visible under a `status=open` filter until the next load, on purpose: the
   *  row vanishing mid-click reads as "did that work?". */
  const replaceRow = (updated: DataAlertRow) => {
    setRows((current) => current.map((row) => (row.id === updated.id ? updated : row)));
  };

  /** Resolving retires the row, so any "still missing" verdict from an earlier recheck goes too. */
  const handleResolved = (updated: DataAlertRow) => {
    replaceRow(updated);
    setRecheckOutcome((current) => (current?.id === updated.id ? null : current));
  };

  const handleRecheck = async (row: DataAlertRow) => {
    if (recheckingId) return;
    setRecheckingId(row.id);
    setRecheckOutcome(null);
    try {
      const { alert, cleared } = await recheckDataAlert(row.id);
      replaceRow(alert);
      // A cleared alert announces itself through the status chip, so it needs no message.
      setRecheckOutcome(cleared ? null : { id: row.id, kind: 'still_missing' });
    } catch (err) {
      console.error('Failed to recheck data alert:', err);
      setRecheckOutcome({ id: row.id, kind: 'error' });
    } finally {
      setRecheckingId(null);
    }
  };

  const handleRunComplete = (result: DataAlertRunResult) => {
    setRunResult(result);
    setReloadToken((current) => current + 1);
  };

  const toggleExpanded = (id: string) => {
    setExpandedId((current) => (current === id ? null : id));
  };

  const inputClass = cn(
    'w-full px-3 py-2 rounded-lg border',
    'border-gray-300 dark:border-gray-600',
    'bg-white dark:bg-gray-700',
    primaryText, smallTextSize
  );

  const headerCellClass = cn(tertiaryText, 'text-xs font-medium text-left px-3 py-2 whitespace-nowrap');
  const cellClass = cn(primaryText, smallTextSize, 'px-3 py-2 align-top');

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className={cn(primaryText, 'text-lg', semiBoldText)}>
              {t('adminTesting.dataAlerts.title')}
            </h2>
            <p className={cn(secondaryText, smallTextSize, 'mt-1')}>
              {t('adminTesting.dataAlerts.description')}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <AskCustomerButton seed={{ type: 'supplier_order_confirmation' }} />
            <button onClick={() => setRunDialogOpen(true)} className={primaryButtonClass}>
              {t('adminTesting.dataAlerts.runAction')}
            </button>
          </div>
        </div>

        {runResult && (
          <div className={cn(lightGreenBG, 'rounded-xl p-4 flex items-start justify-between gap-3')}>
            <p className={cn(primaryText, smallTextSize)}>
              {t('adminTesting.dataAlerts.runResult', {
                revenueRaised: runResult.missingRevenueDay.raised,
                revenueAutoResolved: runResult.missingRevenueDay.autoResolved,
                supplierRaised: runResult.missingSupplierWeek.raised,
                supplierAutoResolved: runResult.missingSupplierWeek.autoResolved,
              })}
            </p>
            {/* Dismissed by hand rather than on a timer: counts of zero are the interesting
                case, and they are exactly the ones worth re-reading. */}
            <button
              onClick={() => setRunResult(null)}
              className={cn(secondaryText, 'hover:opacity-70 shrink-0')}
              aria-label={t('adminTesting.dataAlerts.runResultDismiss')}
            >
              ✕
            </button>
          </div>
        )}

        <div className={cn(cardClass, 'p-6')}>
          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
            <div>
              <label className={cn(secondaryText, 'text-xs block mb-1')}>
                {t('adminTesting.dataAlerts.filterTypeLabel')}
              </label>
              <select
                value={alertType}
                onChange={(e) => applyFilter(setAlertType, e.target.value)}
                className={inputClass}
              >
                <option value="">{t('adminTesting.dataAlerts.filterTypeAll')}</option>
                {DATA_ALERT_TYPES.map((value) => (
                  <option key={value} value={value}>{typeLabel(value, t)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className={cn(secondaryText, 'text-xs block mb-1')}>
                {t('adminTesting.dataAlerts.filterStatusLabel')}
              </label>
              <select
                value={status}
                onChange={(e) => applyFilter(setStatus, e.target.value)}
                className={inputClass}
              >
                <option value="">{t('adminTesting.dataAlerts.filterStatusAll')}</option>
                {DATA_ALERT_STATUSES.map((value) => (
                  <option key={value} value={value}>{t(STATUS_LABEL_KEYS[value])}</option>
                ))}
              </select>
            </div>

            <div>
              <label className={cn(secondaryText, 'text-xs block mb-1')}>
                {t('adminTesting.dataAlerts.filterRestaurantLabel')}
              </label>
              <input
                type="text"
                placeholder={t('adminTesting.dataAlerts.filterRestaurantPlaceholder')}
                value={restaurantQuery}
                onChange={(e) => applyFilter(setRestaurantQuery, e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          {hasFilters && (
            <button
              onClick={clearFilters}
              className={cn(secondaryText, 'text-xs underline mb-3')}
            >
              {t('adminTesting.dataAlerts.clearFilters')}
            </button>
          )}

          {loading ? (
            <p className={cn(secondaryText, smallTextSize, 'py-6 text-center')}>
              {t('adminTesting.dataAlerts.loading')}
            </p>
          ) : error ? (
            <p className={cn(smallTextSize, 'py-6 text-center text-red-600 dark:text-red-400')}>
              {t('adminTesting.dataAlerts.error')}
            </p>
          ) : visibleRows.length === 0 ? (
            <p className={cn(secondaryText, smallTextSize, 'py-6 text-center')}>
              {hasFilters
                ? t('adminTesting.dataAlerts.emptyFiltered')
                : t('adminTesting.dataAlerts.empty')}
            </p>
          ) : (
            <ResponsiveTable
              desktop={
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className={cn('border-b', borderPrimary)}>
                        <th className={headerCellClass}>{t('adminTesting.dataAlerts.columnDetectedAt')}</th>
                        <th className={headerCellClass}>{t('adminTesting.dataAlerts.columnType')}</th>
                        <th className={headerCellClass}>{t('adminTesting.dataAlerts.columnRestaurant')}</th>
                        <th className={headerCellClass}>{t('adminTesting.dataAlerts.columnScope')}</th>
                        <th className={headerCellClass}>{t('adminTesting.dataAlerts.columnStatus')}</th>
                        <th className={headerCellClass} />
                      </tr>
                    </thead>
                    <tbody>
                      {pagedRows.map((row) => (
                        <Fragment key={row.id}>
                        <tr className={cn('border-b', borderPrimary)}>
                          <td className={cn(cellClass, 'whitespace-nowrap')}>{formatDateTime(row.detectedAt)}</td>
                          <td className={cellClass}>{typeLabel(row.alertType, t)}</td>
                          <td className={cellClass}>{row.restaurantName}</td>
                          <td className={cellClass}>
                            <span className="block">{row.scopeKey}</span>
                            <span className={cn(tertiaryText, 'text-xs')}>{row.periodKey}</span>
                          </td>
                          <td className={cellClass}>
                            <StatusChip status={row.status} label={t(STATUS_LABEL_KEYS[row.status])} />
                            <ResolutionNote row={row} t={t} className="block mt-1 max-w-xs" />
                          </td>
                          <td className={cn(cellClass, 'text-right')}>
                            <RowActions
                              row={row}
                              expanded={expandedId === row.id}
                              recheckingId={recheckingId}
                              recheckOutcome={recheckOutcome}
                              onToggle={() => toggleExpanded(row.id)}
                              onRecheck={() => handleRecheck(row)}
                              onResolve={() => setResolving(row)}
                              t={t}
                              className="justify-end"
                            />
                          </td>
                        </tr>
                        {expandedId === row.id && (
                          <tr className={cn('border-b', borderPrimary)}>
                            {/* Full width: the detail is prose + raw JSON, far too wide for the
                                actions column the toggle lives in. */}
                            <td colSpan={6} className={cn(cellClass, 'pb-4')}>
                              <AlertDetail row={row} t={t} />
                            </td>
                          </tr>
                        )}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>

                  {/* Mobile gets the same affordance from MobileCardList's load-more mode. */}
                  <div className="flex items-center justify-between gap-3 mt-4">
                    <span className={cn(secondaryText, 'text-xs')}>
                      {t('adminTesting.dataAlerts.showingRange', {
                        shown: pagedRows.length,
                        total: visibleRows.length,
                      })}
                    </span>
                    {hasMore && (
                      <button
                        onClick={() => setVisibleCount((current) => current + PAGE_SIZE)}
                        className={cn(
                          'px-3 py-1.5 rounded-lg transition-colors',
                          'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600',
                          primaryText, 'text-xs'
                        )}
                      >
                        {t('adminTesting.dataAlerts.loadMore')}
                      </button>
                    )}
                  </div>
                </div>
              }
              mobile={
                <MobileCardList
                  items={pagedRows}
                  keyOf={(row) => row.id}
                  onLoadMore={() => setVisibleCount((current) => current + PAGE_SIZE)}
                  hasMore={hasMore}
                  loadMoreLabel={t('adminTesting.dataAlerts.loadMore')}
                  renderCard={(row) => (
                    <div className={cn('border rounded-xl p-3', borderPrimary)}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <span className={cn(primaryText, smallTextSize, semiBoldText, 'min-w-0 truncate')}>
                          {formatDateTime(row.detectedAt)}
                        </span>
                        <span className="shrink-0">
                          <StatusChip status={row.status} label={t(STATUS_LABEL_KEYS[row.status])} />
                        </span>
                      </div>
                      <div className="flex mb-2">
                        <MobileCardField
                          label={t('adminTesting.dataAlerts.columnType')}
                          value={typeLabel(row.alertType, t)}
                          divider
                        />
                        <MobileCardField
                          label={t('adminTesting.dataAlerts.columnScope')}
                          value={`${row.scopeKey} · ${row.periodKey}`}
                        />
                      </div>
                      <MobileCardField
                        label={t('adminTesting.dataAlerts.columnRestaurant')}
                        value={row.restaurantName}
                      />
                      <ResolutionNote row={row} t={t} className="block mt-2" />
                      <RowActions
                        row={row}
                        expanded={expandedId === row.id}
                        recheckingId={recheckingId}
                        recheckOutcome={recheckOutcome}
                        onToggle={() => toggleExpanded(row.id)}
                        onRecheck={() => handleRecheck(row)}
                        onResolve={() => setResolving(row)}
                        t={t}
                        className="mt-3"
                      />
                      {expandedId === row.id && (
                        <div className="mt-3">
                          <AlertDetail row={row} t={t} />
                        </div>
                      )}
                    </div>
                  )}
                />
              }
            />
          )}
        </div>
      </div>

      {runDialogOpen && (
        <RunDetectorsDialog
          onClose={() => setRunDialogOpen(false)}
          onComplete={handleRunComplete}
          t={t}
        />
      )}

      {resolving && (
        <ResolveDialog
          alert={resolving}
          onClose={() => setResolving(null)}
          onResolved={handleResolved}
          t={t}
        />
      )}
    </AdminLayout>
  );
};

export default AdminCommunicationDataAlertsPage;
